/**
 * Attention threshold keys for type safety
 * Use these constants instead of string literals to prevent typos
 */
export const THRESHOLD_KEYS = {
  DUE_SOON_DAYS: 'DUE_SOON_DAYS',
  DUE_SOON_WARNING_DAYS: 'DUE_SOON_WARNING_DAYS',
  STUCK_HOURS: 'STUCK_HOURS',
  AGING_DAYS: 'AGING_DAYS'
}

/**
 * Attention threshold constants
 * Define when tasks should appear in attention categories
 *
 * ⚠️ IMPORTANT: If you change these default values, you MUST also update:
 * - Migration 044: supabase/migrations/044_add_attention_thresholds_to_user_settings.sql
 * - The SQL default values in the ALTER TABLE and CREATE OR REPLACE FUNCTION statements
 *
 * This ensures new users get consistent defaults whether set by SQL or application code.
 */
export const ATTENTION_THRESHOLDS = {
  [THRESHOLD_KEYS.DUE_SOON_DAYS]: 3,           // Tasks due within this many days
  [THRESHOLD_KEYS.DUE_SOON_WARNING_DAYS]: 2,   // Tasks due within this many days (warning color)
  [THRESHOLD_KEYS.STUCK_HOURS]: 72,            // Tasks in same status for this many hours
  [THRESHOLD_KEYS.AGING_DAYS]: 7               // Tasks created this many days ago
}

/**
 * Helper to get attention threshold value
 * Supports future user-configurable settings
 * @param {string} type - Threshold type (use THRESHOLD_KEYS constants)
 * @param {Object|null} userSettings - Optional user settings object
 * @param {Object} [userSettings.attentionThresholds] - User's custom threshold overrides
 * @returns {number} - Threshold value
 */
export const getAttentionThreshold = (type, userSettings = null) => {
  // Validate that the requested type exists in defaults
  if (!(type in ATTENTION_THRESHOLDS)) {
    console.warn(`[taskDisplayUtils] Invalid attention threshold type: "${type}". Using DUE_SOON_DAYS as fallback.`)
    return ATTENTION_THRESHOLDS[THRESHOLD_KEYS.DUE_SOON_DAYS]
  }

  // Check for user-configured threshold override
  if (userSettings?.attentionThresholds) {
    return userSettings.attentionThresholds[type] || ATTENTION_THRESHOLDS[type]
  }

  // Return default value
  return ATTENTION_THRESHOLDS[type]
}

/**
 * Get the color class for an effort level dot
 * @param {number} effortLevel - The effort level (1-3)
 * @returns {string} - Tailwind color class
 */
export function getEffortLevelColor(effortLevel) {
  if (effortLevel === 1) return 'bg-status-success'
  if (effortLevel === 2) return 'bg-status-warning'
  if (effortLevel === 3) return 'bg-status-danger'
  return 'bg-border'
}
